import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createChecksum } from "../../../commonFunctions/fileFolderConfig";
import { getGridLayoutForDesktop, getGridsOfFormFields } from "../../../commonFunctions/formFunc";
import { useAppOrgContext } from "../../../context/AppOrgContext";
import axiosInstance, { handleApiError } from "../../../services/axiosService";
import { useFormContext } from "../../../context/FormContext";
import Loader from "../../../Layouts/Loader/Loader";
import BuilderModal from "../../../Layouts/Modal/BuilderModal";
import { FormLayoutWithEdit } from "../FormSection";
import RightSideFieldsBar from "../RightSideFieldsBar";
import SubFormSettings from "./SubFormSettings";
import { useTableContext } from "../../../context/TableContext";
import { useNavigate, useParams } from "react-router-dom";
import { toastSuccess } from "../../../utils/toastUtils";
import { getFolderFileById } from "../../../utils/treeData/folderEditorUtils";
import { useTreeContext } from "../../../context/TreeDataContext";
import GenericActionBar from "../../../Layouts/GenericActionBar/GenericActionBar";
import useDirtyChecker from "../../../hooks/useDirtyChecker";
import BuilderTabs from "../../../Layouts/BuilderTabs";
import OnChangeCustomJs from "../OnChangeCustomJs/OnChangeCustomJs";

const SubFormSection = () => {
  const { currentChecksum } = useAppOrgContext();
  const [isLoader, setIsLoader] = useState(true);
  const [error, setError] = useState(null);

  const {
    renderFieldsData,
    setSelectedForm,
    setRenderFieldsArray,
    subFormSettingData,
    formSectionsData,
    setSelectedSectionData
  } = useFormContext();

  const { subformId } = useParams();
  const { getSingleTableData } = useTableContext();
  const navigate = useNavigate();

  const [tableID, setTableID] = useState("");
  const [tableName, setTableName] = useState("");
  const { formsExplorerData } = useTreeContext();

  const fetchSubform = async () => {
    try {
      setIsLoader(true);
      setError(null);

      const { data } = await axiosInstance.get(`/subform/${subformId}`);
      const tableData = getSingleTableData(data.config.table_id);

      setTableName(tableData.data.table.table_name);
      setTableID(data.config.table_id);
      console.log("data", data);

      renderFieldsData.current = {};
      subFormSettingData.current = {
        settings: data.config.settings
      };

      const formLikeData = makeFormSectionData(
        data.config.fields || [],
        data.config?.desktop_layout || {},
        data.config?.mobile_layout || {}
      );

      const gridArray = getGridsOfFormFields(data.config.fields || []);

      renderFieldsData.current = {};
      formSectionsData.current = formLikeData.map((sec) => {
        const fields = sec.fields;

        const newDesktopLayout = getGridLayoutForDesktop(fields, sec.desktop_layout || {});

        const newMobileLayout = getGridLayoutForDesktop(fields, sec.mobile_layout || {});

        const mappedFields = Object.fromEntries(fields.map((field) => [field.id, { ...field }]));

        renderFieldsData.current = {
          ...renderFieldsData.current,
          ...mappedFields
        };

        return {
          ...sec,
          desktop_layout: newDesktopLayout,
          mobile_layout: newMobileLayout
        };
      });
      formSectionsData.current[0].onchange_custom_js = data.config.onchange_custom_js;
      setSelectedSectionData(0);
      setRenderFieldsArray(gridArray);

      const initialDataObj = {
        fields: data.config.fields,
        desktop_layout: data.config.desktop_layout,
        mobile_layout: data.config.mobile_layout,
        settings: data.config.settings
      };

      currentChecksum.current = createChecksum(initialDataObj);
      setIsLoader(false);
    } catch (err) {
      handleApiError(err);
      setError("Failed to load subform. Please try again.");
      setIsLoader(false);
    }
  };

  const makeFormSectionData = (fields, desktopLayout, mobileLayout) => {
    return [
      {
        name: "Section 1",
        fields,
        progress_bar_title: "S1",
        desktop_layout: desktopLayout,
        mobile_layout: mobileLayout,
        key: "Section_123"
      }
    ];
  };

  useEffect(() => {
    fetchSubform();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subformId]);

  const initialRendering = async () => {
    const fileId = `subform/${subformId}`;
    const file = getFolderFileById(formsExplorerData, fileId);
    if (file) {
      setSelectedForm({
        id: fileId,
        name: file.name,
        parentData: file.parentData,
        type: file.type
      });
    } else {
      navigate("../");
    }
  };

  useEffect(() => {
    initialRendering();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subformId]);

  if (isLoader) return <Loader />;
  if (error) return <div className="p-4 text-red-600 bg-red-50 rounded-md">{error}</div>;

  return <SubFormEditor tableID={tableID} tableName={tableName} />;
};

const SubFormEditor = ({ tableID, tableName }) => {
  const [dragFromOutside, setDragFromOutside] = useState(false);
  const [isSubFormOpen, setIsSubFormOpen] = useState(false);
  const [activeMode, setActiveMode] = useState("edit");
  const [isSaving, setIsSaving] = useState(false);

  const { subformId } = useParams();
  const { renderFieldsData, renderFieldsArray, subFormSettingData, formSectionsData } = useFormContext();

  // ----- Dirty Checker -----
  const sortedFields = [...renderFieldsArray].sort((a, b) => a.y - b.y);
  const currentConfig = {
    fields: sortedFields.map((field) => renderFieldsData.current[field.i]),
    settings: subFormSettingData.current?.settings,
    desktop_layout: formSectionsData.current?.[0]?.desktop_layout,
    mobile_layout: formSectionsData.current?.[0]?.mobile_layout,
    onchange_custom_js: formSectionsData.current?.[0]?.onchange_custom_js
  };

  const { isDirty, markClean } = useDirtyChecker(currentConfig);

  const onChangeActiveMode = (value) => {
    setActiveMode(value);
  };

  const handleDragStart = useCallback(
    (e, name) => {
      e.dataTransfer.setData("type", name);
      setDragFromOutside(true);
    },
    [setDragFromOutside]
  );

  const handleSaveSubForm = async () => {
    try {
      setIsSaving(true);

      const arrayCopy = [...renderFieldsArray];
      arrayCopy.sort((a, b) => a.y - b.y);
      const newArray = arrayCopy.map((field) => {
        return renderFieldsData.current[field.i];
      });

      const config = {
        fields: newArray,
        settings: subFormSettingData.current?.settings,
        desktop_layout: formSectionsData.current?.[0]?.desktop_layout,
        mobile_layout: formSectionsData.current?.[0]?.mobile_layout,
        onchange_custom_js: formSectionsData.current?.[0]?.onchange_custom_js
      };

      const { message } = await axiosInstance.patch(`/subform/${subformId}`, {
        config
      });

      toastSuccess(message);
      markClean(); // reset dirty state after successful save
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsSaving(false);
    }
  };

  console.log(formSectionsData);
  console.log(formSectionsData.current?.[0]?.onchange_custom_js);
  const tabs = [
    {
      label: "Form",
      content: (
        <div className="form_outer_section">
          <FormLayoutWithEdit
            dragFromOutside={dragFromOutside}
            setDragFromOutside={setDragFromOutside}
            from="subform"
            tableID={tableID}
            onChangeActiveMode={onChangeActiveMode}
          />
        </div>
      )
    },
    {
      label: "OnChange CustomJS",
      content: <OnChangeCustomJs from="subform" />
    }
  ];

  const memoizedRightSideFieldsBar = useMemo(() => {
    return <RightSideFieldsBar from="subform" handleDragStart={handleDragStart} />;
  }, [handleDragStart]);

  return (
    <div className="sub-form-section w_100">
      <div>
        <GenericActionBar
          path={
            <div>
              SM → <span>{tableName}</span>
            </div>
          }
          actions={[
            {
              key: "settings",
              icon: "settings",
              title: "Settings",
              onClick: () => setIsSubFormOpen(true)
            },
            {
              key: "save",
              icon: "save",
              title: isSaving ? "Saving..." : "Save",
              onClick: isSaving ? undefined : handleSaveSubForm
            }
          ]}
          options={{
            save: {
              className: `${isDirty ? "save_glowing" : ""} ${isSaving ? "disabled" : ""}`.trim()
            }
          }}
        />

        <div className="sub_form_main_section">
          <BuilderTabs tabs={tabs} tabsClass="w_fit px_5" containerClass="px_4" contentClass="builder-tabs-content" />
        </div>
      </div>

      {activeMode === "edit" && <div className="rightsidebar">{memoizedRightSideFieldsBar}</div>}

      <BuilderModal isOpen={isSubFormOpen} onClose={() => setIsSubFormOpen(false)} modalName="Edit Subform Setting">
        <SubFormSettings
          subFormSettingData={subFormSettingData}
          onClose={() => setIsSubFormOpen(false)}
          tableId={tableID}
        />
      </BuilderModal>
    </div>
  );
};

export default SubFormSection;
